import { ref, computed } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { campaignsClient, automationRulesClient, groupAutomationRulesClient } from '~/services';
import { isCampaign } from '~/utils/analytics';
import { routes } from '~/routes';

export const useResourcesDetails = createGlobalState(() => {
    const pendingRequests = ref(0);
    const loading = computed(() => pendingRequests.value > 0);
    const error = ref();

    const resourcesDetails = ref({
        campaign: {},
        automation: {},
    });

    const reset = () => {
        resourcesDetails.value.automation = {};
        resourcesDetails.value.campaign = {};
    };

    routes.afterEach((to, from) => {
        if (to.path !== from?.path) {
            reset();
        }
    });

    /**
     * @param {number} teamId
     * @param {Array<{resource_type: string, resource_id: number}>} resources
     * @param {string|null|undefined} groupId
     * @param {number|null|undefined} orgUnitId
     * @param {object} config
     */
    const fetchResourceDetails = async (teamId, resources, groupId, orgUnitId, config = {}) => {
        if (!resources || !resources.length) {
            return;
        }

        const fetchByType = {
            campaign: [],
            automation: [],
        };

        resources.forEach((resource) => {
            const normalizedType = normalizedResourceType(resource.resource_type);

            // skip if type is not supported
            if (!normalizedType) {
                return;
            }

            // skip fetching if already exists, null means they are going to be worked on
            if (resourcesDetails.value[normalizedType]?.[resource.resource_id] !== undefined) {
                return;
            }

            fetchByType[normalizedType].push(resource.resource_id);

            // mark as null to avoid making two requests
            resourcesDetails.value[normalizedType][resource.resource_id] = null;
        });

        // Split resourceIds into chunks of 10 for each resource type
        const CHUNK_SIZE = 10;
        const requests = [];

        Object.entries(fetchByType).forEach(([resourceType, resourceIds]) => {
            for (let i = 0; i < resourceIds.length; i += CHUNK_SIZE) {
                pendingRequests.value++;
                const idChunk = resourceIds.slice(i, i + CHUNK_SIZE);
                let request;

                if (!orgUnitId && !groupId) {
                    // Corporate level
                    request =
                        resourceType === 'automation'
                            ? automationRulesClient.automationRulesDetails(
                                  teamId,
                                  { automation_rule_ids: idChunk },
                                  config
                              )
                            : campaignsClient.campaignsDetails(teamId, { campaign_ids: idChunk }, config);
                } else if (orgUnitId) {
                    // Org unit level
                    // TODO?: org unit at this moment doesnt have automations
                    request =
                        resourceType === 'automation'
                            ? Promise.resolve({ data: { data: [] } })
                            : campaignsClient.orgUnitCampaignsDetails(
                                  teamId,
                                  orgUnitId,
                                  { campaign_ids: idChunk },
                                  config
                              );
                } else {
                    // Group level
                    request =
                        resourceType === 'automation'
                            ? groupAutomationRulesClient.automationRulesDetails(
                                  teamId,
                                  groupId,
                                  { automation_rule_ids: idChunk },
                                  config
                              )
                            : campaignsClient.groupCampaignsDetails(teamId, groupId, { campaign_ids: idChunk }, config);
                }

                request
                    .then((response) => {
                        response.data.data.forEach((detail) => {
                            resourcesDetails.value[resourceType][detail.id] = detail;
                        });
                    })
                    .finally(() => pendingRequests.value--);

                requests.push(request);
            }
        });

        return Promise.all(requests);
    };

    const getResourceDetail = (resourceType, resourceId) => {
        const normalizedType = normalizedResourceType(resourceType);
        if (!normalizedType) {
            return null;
        }
        return resourcesDetails.value[normalizedType]?.[resourceId] || null;
    };

    const normalizedResourceType = (resourceType) => {
        switch (true) {
            case isCampaign(resourceType):
                return 'campaign';
            case resourceType === 'automation':
                return 'automation';
            default:
                return null;
        }
    };

    return {
        loading,
        error,
        resourcesDetails,
        getResourceDetail,
        fetchResourceDetails,
        reset,
    };
});
